import { getActiveGrns, getPoGrnBlockReason } from './grnLifecycle';

function norm(value) {
  return String(value || '').trim().toUpperCase();
}

export function hasDisplayValue(value) {
  return value != null && String(value).trim() !== '';
}

/** PO statuses eligible for creating a GRN against the order. */
export const GRN_ELIGIBLE_PO_STATUSES = ['APPROVED', 'SENT_TO_VENDOR', 'PARTIALLY_RECEIVED'];

export function isPoEligibleForGrn(po) {
  if (!po) return false;
  return GRN_ELIGIBLE_PO_STATUSES.includes(norm(po.status));
}

function numberOf(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function computePoPendingSummary(poItems = []) {
  let totalPending = 0;
  let hasAnyReceipt = false;
  let hasAnyPending = false;
  let allPendingZero = poItems.length > 0;

  for (const item of poItems) {
    const pending = item?.pending_quantity != null
      ? Math.max(0, Math.floor(numberOf(item.pending_quantity)))
      : null;
    const received = numberOf(item?.received_quantity);
    if (received > 0) hasAnyReceipt = true;
    if (pending != null) {
      totalPending += pending;
      if (pending > 0) hasAnyPending = true;
      if (pending !== 0) allPendingZero = false;
    } else {
      allPendingZero = false;
    }
  }

  return {
    totalPending,
    hasAnyReceipt,
    hasAnyPending,
    allPendingZero,
  };
}

export function derivePoLifecycle(po, poItems = [], grns = []) {
  const rawStatus = norm(po?.status);
  const summary = computePoPendingSummary(poItems);
  const hasGrn = Array.isArray(grns) && grns.length > 0;
  const hasActiveGrn = getActiveGrns(grns).length > 0;

  const payload = po?.po_payload && typeof po.po_payload === 'object' ? po.po_payload : {};
  const paymentStatus = norm(po?.payment_status || payload.payment_status);
  const invoiceStatus = norm(po?.invoice_status || payload.invoice_status);
  const hasInvoice = Boolean(
    po?.invoice_id
    || po?.purchase_invoice_id
    || payload.invoice_id
    || payload.purchase_invoice_id
    || ['INVOICE_GENERATED', 'INVOICED'].includes(rawStatus)
    || ['INVOICE_GENERATED', 'INVOICED'].includes(invoiceStatus)
  );
  const paymentCompleted = Boolean(
    ['PAYMENT_COMPLETED', 'PAID', 'SETTLED'].includes(rawStatus)
    || ['PAYMENT_COMPLETED', 'PAID', 'SETTLED'].includes(paymentStatus)
  );

  let effectiveStatus = rawStatus;
  if (paymentCompleted) effectiveStatus = 'PAYMENT_COMPLETED';
  else if (hasInvoice) effectiveStatus = 'INVOICE_GENERATED';
  else if (summary.hasAnyReceipt && summary.hasAnyPending) effectiveStatus = 'PARTIALLY_RECEIVED';
  else if (summary.allPendingZero && poItems.length > 0) effectiveStatus = 'FULLY_RECEIVED';

  if (effectiveStatus === 'FULLY_RECEIVED' && ['CLOSED', 'COMPLETED'].includes(rawStatus)) {
    effectiveStatus = rawStatus;
  }

  return {
    rawStatus,
    effectiveStatus,
    hasGrn,
    hasActiveGrn,
    hasInvoice,
    paymentCompleted,
    hasAnyPending: summary.hasAnyPending,
    allPendingZero: summary.allPendingZero,
    totalPending: summary.totalPending,
  };
}

export function getPoAllowedActions(po, poItems = [], grns = []) {
  const lc = derivePoLifecycle(po, poItems, grns);
  const actions = new Set(['view', 'print', 'download']);

  if (lc.paymentCompleted) {
    actions.add('audit');
    return actions;
  }

  if (lc.hasInvoice) {
    return actions;
  }

  if (lc.effectiveStatus === 'PARTIALLY_RECEIVED') {
    actions.add('cancel');
    actions.add('create_grn');
    return actions;
  }

  switch (lc.rawStatus) {
    case 'DRAFT':
      actions.add('edit');
      actions.add('delete');
      actions.add('submit');
      break;
    case 'PENDING_APPROVAL':
    case 'SUBMITTED':
      actions.add('approve');
      actions.add('reject');
      actions.add('cancel');
      break;
    case 'APPROVED':
      actions.add('create_grn');
      if (!lc.hasActiveGrn) actions.add('cancel');
      actions.add('send_to_vendor');
      if (lc.allPendingZero && poItems.length > 0) actions.add('close');
      if (lc.hasAnyPending) actions.add('short_close');
      break;
    case 'SENT_TO_VENDOR':
      actions.add('create_grn');
      if (lc.allPendingZero && poItems.length > 0) actions.add('close');
      else if (lc.hasAnyPending) {
        actions.add('short_close');
        actions.add('cancel');
      }
      break;
    case 'PARTIALLY_RECEIVED':
      actions.add('create_grn');
      actions.add('cancel');
      break;
    case 'FULLY_RECEIVED':
    case 'COMPLETED':
    case 'CLOSED':
      // Read only actions already present.
      break;
    default:
      break;
  }

  return actions;
}

/**
 * Reasons the terminating actions must be disabled, keyed by the same action names
 * getPoAllowedActions() returns. The action stays in the allowed set so the button still
 * renders — the user needs to see why it is unavailable, not have it vanish.
 */
export function getPoActionBlockers(po, poItems = [], grns = []) {
  const blockers = {};
  const grnReason = getPoGrnBlockReason(grns);
  if (grnReason) {
    blockers.short_close = grnReason;
    blockers.cancel = grnReason;
  }
  return blockers;
}

export function getPoDisplayStatus(po, poItems = [], grns = []) {
  const lc = derivePoLifecycle(po, poItems, grns);
  if (lc.effectiveStatus === 'PARTIALLY_RECEIVED') return 'PARTIALLY_RECEIVED';
  if (lc.effectiveStatus === 'FULLY_RECEIVED') return 'COMPLETED';
  return lc.effectiveStatus || lc.rawStatus || '';
}
